import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Form as FinalForm, Field } from 'react-final-form';

import { NamedLink, LocationAutocompleteInput } from '../../../components';
import ListingCard from '../../../components/ListingCard/ListingCard';
import { getMarketplaceEntities } from '../../../ducks/marketplaceData.duck';
import { encodeLatLngBounds } from '../../../util/urlHelpers';

import heroImg from '../../../assets/home/hero-lot.jpg';
import imgDump from '../../../assets/blog-hub/type-dump.webp';
import imgCar from '../../../assets/blog-hub/type-car-hauler.jpg';
import imgEquipment from '../../../assets/blog-hub/type-equipment.jpg';
import imgEnclosed from '../../../assets/blog-hub/type-enclosed.webp';
import imgUtility from '../../../assets/blog-hub/type-utility.webp';
import imgGooseneck from '../../../assets/blog-hub/type-gooseneck.jpg';
import css from './HomeSections.module.css';

// ================================================================
// BigLoads homepage - "Find the Right Trailer. From Someone Right
// Down the Road."  Custom-coded sections (bypasses PageBuilder),
// mirrors the SubscriptionPage pattern. Art direction: Fable +
// Rambo's live activity ticker. Conversion-first, SEO-as-plumbing.
// ================================================================

const isBrowser = typeof window !== 'undefined';

// Trailer types that now have a dedicated SEO hub page (SeoTypePage). For these,
// homepage links point to the hub (/<slug>) instead of the raw /s search URL:
// the hub is the better landing page (ranks + content + funnels to listings)
// and this gives the hubs strong internal links from the homepage. Keep in sync
// with util/typeSeoContent.js. Types NOT here fall back to the search link.
const TYPE_HUB_SLUG = {
  'dump-trailer': 'dump-trailer-rental',
  'motorcycle-trailer': 'motorcycle-trailer-rental',
  'car-hauler': 'car-hauler-rental',
};

// Trailer-type search links use the real `trailerType` multi-enum key
// (from util/listingSpecs.js): ?pub_trailerType=has_any:<key>

// ---------------------------------------------------------------
// Section 1 : HERO  (+ live ticker)
// ---------------------------------------------------------------
const identity = v => v;

const TYPE_OPTIONS = [
  ['', 'Any trailer'],
  ['dump-trailer', 'Dump'],
  ['utility-trailer', 'Utility'],
  ['car-hauler', 'Car Haulers'],
  ['enclosed-trailer', 'Enclosed'],
  ['equipment-trailer', 'Equipment'],
  ['flatbed-trailer', 'Flatbed'],
  ['gooseneck-trailer', 'Gooseneck'],
];

// Hero search. Uses the SAME LocationAutocompleteInput as the topbar so a
// picked place carries real { bounds, origin } -> we build the identical
// ?address=&bounds=&mapSearch=true URL the topbar produces. A plain text
// address (no bounds) is ignored by the marketplace and returns everything,
// which was the v1 bug.
const HeroSearch = () => {
  const [trailerType, setTrailerType] = useState('');

  const onSubmit = values => {
    if (!isBrowser) return;
    const loc = values && values.location;
    const selectedPlace = loc && loc.selectedPlace;
    const params = new URLSearchParams();

    if (selectedPlace && selectedPlace.bounds) {
      // Reuse the canonical encoder (NE lat,NE lng,SW lat,SW lng) so the URL
      // matches the topbar/SearchPage format exactly.
      // NOTE: do NOT set mapSearch here. The topbar omits it, and the
      // SearchPage widen effect (min-span default radius) only runs when
      // mapSearch is absent. Setting it here suppressed the widen, so a
      // small town/zip opened on a tiny viewport with 0 results.
      params.set('address', loc.search || '');
      params.set('bounds', encodeLatLngBounds(selectedPlace.bounds));
    } else if (loc && loc.search) {
      // Fallback: no geocoded bounds (user typed but did not pick a
      // suggestion). Pass the raw text; better than nothing.
      params.set('address', loc.search);
    }
    if (trailerType) {
      params.set('pub_trailerType', `has_any:${trailerType}`);
    }
    const qs = params.toString();
    window.location.assign(qs ? `/s?${qs}` : '/s');
  };

  return (
    <FinalForm
      onSubmit={onSubmit}
      render={({ handleSubmit }) => (
        <form className={css.searchCard} onSubmit={handleSubmit}>
          <div className={css.searchFields}>
            <div className={css.field}>
              <span className={css.fieldLabel}>Location</span>
              <Field
                name="location"
                format={identity}
                render={({ input, meta }) => {
                  const { onChange, ...restInput } = input;
                  return (
                    <LocationAutocompleteInput
                      rootClassName={css.locAutocomplete}
                      iconClassName={css.locIcon}
                      inputClassName={css.fieldInput}
                      predictionsClassName={css.locPredictions}
                      placeholder="City or ZIP"
                      closeOnBlur
                      input={{ ...restInput, onChange }}
                      meta={meta}
                    />
                  );
                }}
              />
            </div>
            <div className={css.field}>
              <span className={css.fieldLabel}>Trailer Type</span>
              <select
                className={css.fieldInput}
                value={trailerType}
                onChange={e => setTrailerType(e.target.value)}
              >
                {TYPE_OPTIONS.map(([v, l]) => (
                  <option key={v || 'any'} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button className={css.searchBtn} type="submit">
            Find Trailers
          </button>
        </form>
      )}
    />
  );
};

const QUICK_PILLS = [
  ['dump-trailer', 'Dump'],
  ['utility-trailer', 'Utility'],
  ['car-hauler', 'Car Haulers'],
  ['equipment-trailer', 'Equipment'],
];

const LiveTicker = ({ listings }) => {
  // Real recent activity strip. Uses real listing titles + cities. Falls back
  // to a curated real-format set if no live listings loaded. Never fabricated
  // beyond honest "listed"/"available" framing of real listings.
  const items =
    listings && listings.length
      ? listings.slice(0, 8).map(l => {
          const title = l?.attributes?.title || 'Trailer';
          const city =
            l?.attributes?.publicData?.location?.address ||
            l?.attributes?.publicData?.city ||
            null;
          return city ? `${title} in ${city}` : `${title} listed`;
        })
      : [
          'Dump trailers available across Texas',
          'Car haulers listed near you',
          'New trailers added every week',
          'Utility trailers ready to rent',
          'Flatbeds available now',
          'Equipment trailers near you',
        ];
  // Duplicate for a seamless marquee loop.
  const loop = [...items, ...items];
  return (
    <div className={css.ticker} aria-hidden="true">
      <div className={css.tickerTrack}>
        {loop.map((t, i) => (
          <span className={css.tickerItem} key={i}>
            <span className={css.tickerDot} />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
};

const SectionHero = ({ listings }) => (
  <section className={css.hero}>
    <div className={css.heroInner}>
      <div className={css.heroCopy}>
        <div className={css.eyebrow}>THE PEER-TO-PEER TRAILER RENTAL MARKETPLACE</div>
        <h1 className={css.heroTitle}>
          Find the Right Trailer.
          <br />
          <span className={css.accent}>From Someone Right Down the Road.</span>
        </h1>
        <p className={css.heroSub}>
          Dump trailers, utility trailers, car haulers, equipment trailers and more. Rent straight
          from real local owners on a peer-to-peer marketplace, not a rental counter.
        </p>
        <HeroSearch />
        <div className={css.pills}>
          {QUICK_PILLS.map(([key, label]) =>
            TYPE_HUB_SLUG[key] ? (
              // Dedicated type hub exists -> link the hub (internal-link + ranking)
              <a key={key} className={css.pill} href={`/${TYPE_HUB_SLUG[key]}`}>
                {label}
              </a>
            ) : (
              <NamedLink
                key={key}
                className={css.pill}
                name="SearchPage"
                to={{ search: `?pub_trailerType=has_any:${key}` }}
              >
                {label}
              </NamedLink>
            )
          )}
        </div>
        <div className={css.trustEcho}>1,200+ trailers. 640+ cities. $0 commission, ever.</div>
      </div>
      <div className={css.heroPhoto} style={{ backgroundImage: `url(${heroImg})` }} />
    </div>
    <div className={css.ownerStrip}>
      <span>Own trailers? Get discovered by local renters and keep 100 percent of every rental.</span>
      <NamedLink className={css.ownerStripBtn} name="OnboardingPage">
        List Your Trailer
      </NamedLink>
    </div>
    <LiveTicker listings={listings} />
  </section>
);

// ---------------------------------------------------------------
// Section 2 : WHAT ARE YOU HAULING?
// ---------------------------------------------------------------
const HAUL_TILES = [
  {
    key: 'dump-trailer',
    title: 'Dirt, Gravel and Debris',
    sub: 'Dump trailers built to dump and go',
    img: imgDump,
  },
  {
    key: 'car-hauler',
    title: 'Cars and Vehicles',
    sub: 'Car haulers with ramps and winch points',
    img: imgCar,
  },
  {
    key: 'equipment-trailer',
    title: 'Equipment and Machinery',
    sub: 'Heavy decks for skid steers and excavators',
    img: imgEquipment,
  },
  {
    key: 'enclosed-trailer',
    title: 'Furniture and Moving',
    sub: 'Enclosed trailers that keep it dry',
    img: imgEnclosed,
  },
  {
    key: 'utility-trailer',
    title: 'Landscaping and General',
    sub: 'Utility trailers for the everyday load',
    img: imgUtility,
  },
  {
    key: 'gooseneck-trailer',
    title: 'Heavy Loads',
    sub: 'Gooseneck and flatbed muscle',
    img: imgGooseneck,
  },
];

const SectionHauling = () => (
  <section className={css.hauling}>
    <div className={css.container}>
      <h2 className={css.sectionH}>What Are You Hauling?</h2>
      <p className={css.sectionSub}>Pick the job. We will show you the trailer.</p>
      <div className={css.tileGrid}>
        {HAUL_TILES.map(t => {
          const inner = (
            <>
              <div className={css.tilePhoto} style={{ backgroundImage: `url(${t.img})` }} />
              <div className={css.tileLabel}>
                <span className={css.tileTitle}>{t.title}</span>
                <span className={css.tileSub}>{t.sub}</span>
              </div>
            </>
          );
          // Dedicated type hub exists -> link the hub (internal-link + ranking)
          return TYPE_HUB_SLUG[t.key] ? (
            <a key={t.key} className={css.tile} href={`/${TYPE_HUB_SLUG[t.key]}`}>
              {inner}
            </a>
          ) : (
            <NamedLink
              key={t.key}
              className={css.tile}
              name="SearchPage"
              to={{ search: `?pub_trailerType=has_any:${t.key}` }}
            >
              {inner}
            </NamedLink>
          );
        })}
      </div>
      <NamedLink className={css.textLink} name="SearchPage">
        Not sure what you need? Browse all trailer types
      </NamedLink>
    </div>
  </section>
);

// ---------------------------------------------------------------
// Section 3 : TRAILERS NEAR YOU  (LIVE)
// ---------------------------------------------------------------
const SectionNearYou = ({ listings }) => {
  const hasListings = listings && listings.length > 0;
  return (
    <section className={css.nearYou}>
      <div className={css.container}>
        <div className={css.rowHead}>
          <div>
            <h2 className={css.sectionH}>Just Listed</h2>
            <p className={css.sectionSub}>
              The newest trailers on BigLoads, straight from their owners' driveways.
            </p>
          </div>
          <NamedLink className={css.textLink} name="SearchPage">
            View all trailers
          </NamedLink>
        </div>
        {/* Shared wrapper reserves the row height so the empty->loaded swap
            (async listings fetch) doesn't shift the page either direction (CLS). */}
        <div className={css.cardZone}>
          {hasListings ? (
            <div className={css.cardRow}>
              {listings.slice(0, 8).map(l => (
                <ListingCard
                  key={l.id.uuid}
                  className={css.homeCard}
                  listing={l}
                  showAuthorInfo={false}
                  renderSizes="(max-width: 767px) 80vw, 280px"
                />
              ))}
            </div>
          ) : (
            <div className={css.cardRowEmpty}>
              <NamedLink className={css.searchBtn} name="SearchPage">
                Browse Trailers
              </NamedLink>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------
// Section 4 : BUILT FOR BOTH SIDES OF THE HAUL
// ---------------------------------------------------------------
const RENTER_STEPS = [
  ['Search', 'Type your city, pick your trailer type.'],
  ['Compare', 'Real photos, real specs, real prices per day.'],
  ['Contact', 'Message the owner, or call Haul Star owners direct.'],
  ['Get Hauling', 'Hitch up and handle your business.'],
];
const OWNER_STEPS = [
  ['Create Your Profile', 'Takes minutes, costs nothing.'],
  ['Add Your Trailers', 'Photos, specs, your price.'],
  ['Get Discovered', 'Local renters are already searching.'],
  ['Keep Every Dollar', '$0 commission. Your rental, your money.'],
];

const StepList = ({ steps, variant }) =>
  steps.map(([title, body], i) => (
    <div className={css.step} key={title}>
      <span className={`${css.stepNum} ${variant === 'owner' ? css.stepNumOwner : ''}`}>
        {String(i + 1).padStart(2, '0')}
      </span>
      <div>
        <div className={css.stepTitle}>{title}</div>
        <div className={css.stepBody}>{body}</div>
      </div>
    </div>
  ));

const SectionBothSides = () => (
  <section className={css.bothSides}>
    <div className={css.container}>
      <h2 className={css.sectionHDark}>Built for Both Sides of the Haul</h2>
      <p className={css.sectionSubDark}>
        Renters get the right trailer fast. Owners get paid in full. Nobody gets skimmed.
      </p>
      <div className={css.twoLane}>
        <div className={css.laneRenter}>
          <div className={css.laneHead}>NEED A TRAILER?</div>
          <StepList steps={RENTER_STEPS} variant="renter" />
          <NamedLink className={css.laneBtnRenter} name="SearchPage">
            Browse Trailers
          </NamedLink>
        </div>
        <div className={css.laneOwner}>
          <div className={css.laneHead}>OWN A TRAILER?</div>
          <StepList steps={OWNER_STEPS} variant="owner" />
          <NamedLink className={css.laneBtnOwner} name="OnboardingPage">
            List Your Trailer
          </NamedLink>
        </div>
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------
// Section 5 : OWNER ANTI-GATEKEEPER PANEL
// ---------------------------------------------------------------
const COMPARE_ROWS = [
  ['Commission on rentals', '$0, ever', 'Typically 15 to 25 percent of every rental'],
  ['Customer contact', 'Renters reach you direct (Haul Star)', 'Routed through their platform'],
  ['Your business info & Google reviews', 'Your logo, phone, website + imported reviews', 'Their brand, not yours'],
  ['Listing costs', 'Free forever, upgrade if you want', 'Free until they take their cut'],
  ['Who owns the customer', 'You do', 'They do'],
  ['Payouts', '100 percent of your price', 'Your price minus their cut'],
];

const SectionOwnerValue = () => (
  <section className={css.ownerValue}>
    <div className={css.container}>
      <div className={css.eyebrowOrange}>FOR TRAILER OWNERS</div>
      <h2 className={css.sectionHLight}>Your Trailers. Your Customers. Your Money.</h2>
      <p className={css.sectionSubLight}>
        The other guys built a <span className={css.tollbooth}>tollbooth</span>. We built you a
        billboard.
      </p>

      <div className={css.valueProps}>
        <div className={css.vprop}>
          <div className={css.vpropH}>$0 Commission</div>
          <p>We never take a cut of your rental. Not 15 percent, not 3 percent. Zero.</p>
        </div>
        <div className={css.vprop}>
          <div className={css.vpropH}>Direct Customer Contact</div>
          <p>
            Haul Star puts your phone number, email and website in front of renters. They call you.
            Not a call center.
          </p>
        </div>
        <div className={css.vprop}>
          <div className={css.vpropH}>Built to Grow</div>
          <p>
            Verified badge, your logo, imported Google reviews and a live link to your website. Your
            listing works like an ad for your business.
          </p>
        </div>
      </div>

      <div className={css.compareTable}>
        <div className={css.compareHeadRow}>
          <div className={css.compareCellFeature}>What You Get</div>
          <div className={css.compareCellUs}>BigLoads</div>
          <div className={css.compareCellThem}>Other Platforms</div>
        </div>
        {COMPARE_ROWS.map(([feat, us, them]) => (
          <div className={css.compareRow} key={feat}>
            <div className={css.compareCellFeature}>{feat}</div>
            <div className={css.compareCellUs}>{us}</div>
            <div className={css.compareCellThem}>{them}</div>
          </div>
        ))}
        <div className={css.gutPunch}>
          Their cut of a $150 rental: typically $22 to $37. Ours: $0.
        </div>
      </div>

      <div className={css.ownerCtas}>
        <NamedLink className={css.btnOrange} name="SubscriptionPage">
          See Haul Star, $30/mo
        </NamedLink>
        <NamedLink className={css.btnOutline} name="OnboardingPage">
          List Free
        </NamedLink>
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------
// Section 6 : TRUST STRIP
// ---------------------------------------------------------------
const PRESS = ['Business Insider', 'AP', 'The Globe and Mail', 'StreetInsider', 'Digital Journal'];
const STATS = [
  '1,200+ trailers listed',
  'Trailers in 640+ cities',
  '$0 commission, ever',
  'Built owner-first',
];

const SectionTrust = () => (
  <section className={css.trust}>
    <div className={css.container}>
      <div className={css.trustKicker}>AS SEEN ON</div>
      <div className={css.pressRow}>
        {PRESS.map(p => (
          <span className={css.pressLogo} key={p}>
            {p}
          </span>
        ))}
      </div>
      <div className={css.statRow}>
        {STATS.map((s, i) => (
          <span key={s} className={`${css.statChip} ${i === 2 ? css.statChipOrange : ''}`}>
            {s}
          </span>
        ))}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------
// Section 7 : ACROSS AMERICA  (SEO mesh)
// ---------------------------------------------------------------
const POPULAR_STATES = [
  ['Texas', 'trailer-rentals-texas'],
  ['Florida', 'trailer-rentals-florida'],
  ['California', 'trailer-rentals-california'],
  ['Georgia', 'trailer-rentals-georgia'],
  ['Tennessee', 'trailer-rentals-tennessee'],
  ['North Carolina', 'trailer-rentals-north-carolina'],
];
const POPULAR_CITIES = [
  ['San Antonio', 'trailer-rentals-texas', 'trailer-rentals-in-san-antonio-texas'],
  ['Austin', 'trailer-rentals-texas', 'trailer-rentals-in-austin-texas'],
  ['Dallas', 'trailer-rentals-texas', 'trailer-rentals-in-dallas-texas'],
  ['Houston', 'trailer-rentals-texas', 'trailer-rentals-in-houston-texas'],
  ['Tampa', 'trailer-rentals-florida', 'trailer-rentals-in-tampa-florida'],
  ['Atlanta', 'trailer-rentals-georgia', 'trailer-rentals-in-atlanta-georgia'],
];

const SectionAcrossAmerica = () => (
  <section className={css.america}>
    <div className={css.container}>
      <h2 className={css.sectionH}>Trailer Rentals Across America</h2>
      <p className={css.sectionSub}>
        From Texas dirt to Florida sand. Find trailers where you actually live.
      </p>
      <div className={css.geoGrid}>
        <div>
          <div className={css.geoLabel}>Popular States</div>
          <div className={css.pillWrap}>
            {POPULAR_STATES.map(([label, state]) => (
              <NamedLink
                key={state}
                className={css.geoPillState}
                name="StateListsPage"
                params={{ state }}
              >
                {label}
              </NamedLink>
            ))}
          </div>
          <NamedLink className={css.textLink} name="SeoCategoriesPage">
            Browse All States
          </NamedLink>
        </div>
        <div>
          <div className={css.geoLabel}>Popular Cities</div>
          <div className={css.pillWrap}>
            {POPULAR_CITIES.map(([label, state, title]) => (
              <NamedLink
                key={title}
                className={css.geoPillCity}
                name="SeoCategoryPage"
                params={{ state, title }}
              >
                {label}
              </NamedLink>
            ))}
          </div>
          <NamedLink className={css.textLink} name="SeoCategoriesPage">
            Browse All Cities
          </NamedLink>
        </div>
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------
// Section 8 : TOOLS & GUIDES
// ---------------------------------------------------------------
// Each tool card now points at a REAL destination: calculators -> standalone
// tool pages, guides -> live blog articles.
const TOOLS = [
  { title: 'Towing Capacity Calculator', body: 'Know what your truck can pull before you book.', kind: 'tool', name: 'TowingCapacityPage' },
  { title: 'Payload Calculator', body: 'Weight, tongue weight, margin. Get it right.', kind: 'tool', name: 'TrailerPayloadPage' },
  { title: 'Rental Income Calculator', body: 'See what your trailer could earn on BigLoads.', kind: 'tool', name: 'SavingsCalculatorPage' },
  { title: 'Trailer Rental Cost Guide', body: 'What a fair daily rate looks like, type by type.', kind: 'guide', name: 'SingleBlogPage', params: { title: 'trailer-rental-cost-guide' } },
  { title: 'How to Price Trailer Rentals', body: 'For owners setting a rate that actually rents.', kind: 'guide', name: 'SingleBlogPage', params: { title: 'how-to-price-trailer-rental' } },
  { title: 'How to Load a Trailer Safely', body: '60/40 rule, straps, and not wrecking your day.', kind: 'guide', name: 'SingleBlogPage', params: { title: 'how-to-load-a-trailer-safely' } },
];

const SectionTools = () => (
  <section className={css.tools}>
    <div className={css.container}>
      <h2 className={css.sectionH}>Tools for the Haul</h2>
      <p className={css.sectionSub}>Free calculators and straight answers. No fluff, no upsell.</p>
      <div className={css.toolRow}>
        {TOOLS.map(t => (
          <NamedLink
            key={t.title}
            className={`${css.toolCard} ${t.kind === 'tool' ? css.toolCardTool : ''}`}
            name={t.name}
            params={t.params}
          >
            <div className={css.toolTitle}>{t.title}</div>
            <div className={css.toolBody}>{t.body}</div>
            <div className={css.toolCta}>{t.kind === 'tool' ? 'Run the Numbers' : 'Read the Guide'}</div>
          </NamedLink>
        ))}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------
// Section 9 : FINAL CTA BAND
// ---------------------------------------------------------------
const SectionFinalCta = ({ hitchInView }) => (
  <section className={css.finalCta}>
    <div className={css.container}>
      <div className={`${css.hitch} ${hitchInView ? css.hitchClunk : ''}`} aria-hidden="true" />
      <h2 className={css.finalH}>READY TO HAUL OR READY TO GROW?</h2>
      <p className={css.finalSub}>
        Renters, your trailer is a few miles away. Owners, your next customer is already searching.
      </p>
      <div className={css.finalCtas}>
        <NamedLink className={css.btnOutlineLight} name="SearchPage">
          Browse Trailers
        </NamedLink>
        <NamedLink className={css.btnOrange} name="OnboardingPage">
          List Your Trailer
        </NamedLink>
      </div>
      <div className={css.finalWhisper}>
        Listing is free forever. Haul Star is $30 a month when you are ready for more.
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------
// Section 10 : ANSWER BLOCK  (crawlable, above app footer)
// ---------------------------------------------------------------
const SectionAnswer = () => (
  <section className={css.answer}>
    <div className={css.container}>
      <div className={css.answerCard}>
        <h2 className={css.answerH}>What is BigLoads?</h2>
        <p>
          BigLoads is a peer-to-peer trailer rental marketplace where local trailer owners list dump
          trailers, utility trailers, car haulers, enclosed trailers and equipment trailers for
          rent, and renters find them nearby. Listing is free forever, and BigLoads never takes a
          commission from a rental. Owners who want more visibility can upgrade to Haul Star for $30
          a month, which adds verification, direct contact with renters, business branding and
          imported reviews. Today BigLoads has more than 1,200 trailers listed across 640+ US
          cities.
        </p>
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------
// The Haul Line (signature) - scroll-drawn orange SVG spine.
// SSR-safe: only animates in the browser, respects reduced-motion.
// v1 scope: continuous scroll-drawn line + hitch clunk at the end.
// ---------------------------------------------------------------
const useScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!isBrowser) return undefined;
    const reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setProgress(1);
      return undefined;
    }
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        const doc = document.documentElement;
        const max = doc.scrollHeight - window.innerHeight;
        const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
        setProgress(p);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);
  return progress;
};

// ---------------------------------------------------------------
// Root
// ---------------------------------------------------------------
const HomeSections = () => {
  const homeListingRefs = useSelector(state => state.LandingPage?.homeListingRefs || []);
  const listings = useSelector(state => getMarketplaceEntities(state, homeListingRefs));

  const progress = useScrollProgress();
  const hitchInView = progress > 0.9;

  // The Haul Line: a fixed vertical rail whose orange fill height tracks scroll.
  const railRef = useRef(null);

  return (
    <div className={css.page}>
      {/* Haul Line spine (decorative) */}
      <div className={css.haulRail} ref={railRef} aria-hidden="true">
        <div className={css.haulFill} style={{ height: `${Math.round(progress * 100)}%` }} />
      </div>

      <SectionHero listings={listings} />
      <SectionHauling />
      <SectionNearYou listings={listings} />
      <SectionBothSides />
      <SectionOwnerValue />
      <SectionTrust />
      <SectionAcrossAmerica />
      <SectionTools />
      <SectionFinalCta hitchInView={hitchInView} />
      <SectionAnswer />
    </div>
  );
};

export default HomeSections;
